<template>
  <div class="hierarchical-select">
    <div
      class="form-control hierarchical-select-trigger"
      :class="{ 'disabled': disabled }"
      @click="toggleDropdown"
    >
      <div class="selected-value">
        <span v-if="selectedLabel">{{ selectedLabel }}</span>
        <span
          v-else
          class="text-muted"
        >{{ placeholder }}</span>
      </div>
      <i
        class="bi"
        :class="isOpen ? 'bi-chevron-up' : 'bi-chevron-down'"
      />
    </div>

    <div
      v-if="isOpen"
      class="hierarchical-dropdown"
      v-click-outside="closeDropdown"
    >
      <div
        v-if="searchable"
        class="search-box"
      >
        <input
          ref="searchInput"
          v-model="searchQuery"
          type="text"
          class="form-control form-control-sm"
          :placeholder="searchPlaceholder"
          @input="handleSearch"
        >
        <i class="bi bi-search search-icon" />
      </div>

      <div class="tree-container">
        <q-tree
          ref="treeRef"
          :nodes="filteredNodes"
          node-key="id"
          :selected="internalSelected"
          :no-connectors="true"
          @update:selected="handleSelect"
          :filter="searchQuery"
          :no-results-label="noResultsLabel"
          :no-nodes-label="noNodesLabel"
        >
          <template #default-header="prop">
            <div class="tree-node-content">
              <i
                class="bi node-icon"
                :class="getNodeIcon(prop.node)"
                :style="{ color: getNodeColor(prop.node) }"
              />
              <span class="node-label">{{ prop.node.label }}</span>
              <span
                v-if="prop.node.badge"
                class="badge bg-secondary ms-2"
              >
                {{ prop.node.badge }}
              </span>
            </div>
          </template>
        </q-tree>

        <div
          v-if="allowNull"
          class="clear-selection"
        >
          <button
            type="button"
            class="btn btn-sm btn-outline-secondary w-100"
            @click="handleClear"
          >
            <i class="bi bi-x-circle" /> {{ clearLabel }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, watch, nextTick, PropType } from 'vue'
import { QTree } from 'quasar'

interface TreeNode {
  id: number | string | null
  label: string
  children?: TreeNode[]
  depth?: number
  disabled?: boolean
  badge?: string | number
  icon?: string
  color?: string
}

// Click outside directive (registered on component)
const vClickOutside = {
  mounted(el: HTMLElement & { clickOutsideEvent?: (e: Event) => void }, binding: any) {
    el.clickOutsideEvent = (event: Event) => {
      const root = el.closest('.hierarchical-select') || el
      if (!(root === event.target || root.contains(event.target as Node))) {
        binding.value()
      }
    }
    document.addEventListener('click', el.clickOutsideEvent)
  },
  unmounted(el: HTMLElement & { clickOutsideEvent?: (e: Event) => void }) {
    if (el.clickOutsideEvent) {
      document.removeEventListener('click', el.clickOutsideEvent)
    }
  }
}

export default defineComponent({
  name: 'HierarchicalSelect',
  components: {
    QTree
  },
  directives: {
    clickOutside: vClickOutside
  },
  props: {
    modelValue: {
      type: [Number, String, null] as PropType<number | string | null>,
      default: null
    },
    nodes: {
      type: Array as PropType<TreeNode[]>,
      required: true
    },
    placeholder: {
      type: String,
      default: 'Select an option'
    },
    searchPlaceholder: {
      type: String,
      default: 'Search...'
    },
    clearLabel: {
      type: String,
      default: 'Clear selection'
    },
    noResultsLabel: {
      type: String,
      default: 'No results found'
    },
    noNodesLabel: {
      type: String,
      default: 'No options available'
    },
    searchable: {
      type: Boolean,
      default: true
    },
    allowNull: {
      type: Boolean,
      default: true
    },
    disabled: {
      type: Boolean,
      default: false
    }
  },
  emits: ['update:modelValue'],
  setup(props, { emit }) {
    const isOpen = ref(false)
    const searchQuery = ref('')
    const internalSelected = ref<number | string | null>(props.modelValue)
    const treeRef = ref<InstanceType<typeof QTree> | null>(null)
    const searchInput = ref<HTMLInputElement | null>(null)

    // Convert flat nodes to tree structure with proper formatting
    const formattedNodes = computed(() => {
      return formatNodesForTree(props.nodes)
    })

    const filteredNodes = computed(() => {
      if (!searchQuery.value.trim()) {
        return formattedNodes.value
      }
      return filterTreeNodes(formattedNodes.value, searchQuery.value.toLowerCase())
    })

    const selectedLabel = computed(() => {
      if (internalSelected.value === null) {
        return ''
      }
      const node = findNodeById(formattedNodes.value, internalSelected.value)
      return node ? node.label : ''
    })

    function formatNodesForTree(nodes: TreeNode[]): TreeNode[] {
      return nodes.map(node => ({
        ...node,
        id: node.id ?? 'null',
        selectable: !node.disabled,
        children: node.children ? formatNodesForTree(node.children) : undefined
      }))
    }

    function findNodeById(nodes: TreeNode[], id: number | string | null): TreeNode | null {
      for (const node of nodes) {
        if (node.id === id) {
          return node
        }
        if (node.children) {
          const found = findNodeById(node.children, id)
          if (found) return found
        }
      }
      return null
    }

    function filterTreeNodes(nodes: TreeNode[], query: string): TreeNode[] {
      const filtered: TreeNode[] = []

      for (const node of nodes) {
        const matches = node.label.toLowerCase().includes(query)
        const childrenFiltered = node.children ? filterTreeNodes(node.children, query) : []

        if (matches || childrenFiltered.length > 0) {
          filtered.push({
            ...node,
            children: childrenFiltered.length > 0 ? childrenFiltered : node.children
          })
        }
      }

      return filtered
    }

    function getNodeIcon(node: TreeNode): string {
      if (node.icon) {
        return node.icon
      }
      return node.children && node.children.length > 0
        ? 'bi-folder'
        : 'bi-file-earmark'
    }

    function getNodeColor(node: TreeNode): string {
      if (node.color) {
        return node.color
      }
      const depth = node.depth ?? 0
      const colors = ['#2563eb', '#7c3aed', '#db2777', '#ea580c', '#059669']
      return colors[depth % colors.length]
    }

    function toggleDropdown() {
      if (props.disabled) {
        return
      }
      isOpen.value = !isOpen.value
      if (isOpen.value) {
        nextTick(() => {
          if (searchInput.value) {
            searchInput.value.focus()
          }
        })
      }
    }

    function closeDropdown() {
      isOpen.value = false
      searchQuery.value = ''
    }

    function handleSelect(nodeId: string | number) {
      const actualValue = nodeId === 'null' ? null : nodeId
      internalSelected.value = actualValue
      emit('update:modelValue', actualValue)
      closeDropdown()
    }

    function handleClear() {
      internalSelected.value = null
      emit('update:modelValue', null)
      closeDropdown()
    }

    function handleSearch() {
      // Quasar QTree handles filtering automatically via :filter prop
    }

    watch(() => props.modelValue, (newVal) => {
      internalSelected.value = newVal
    })

    return {
      isOpen,
      searchQuery,
      internalSelected,
      treeRef,
      searchInput,
      filteredNodes,
      selectedLabel,
      toggleDropdown,
      closeDropdown,
      handleSelect,
      handleClear,
      handleSearch,
      getNodeIcon,
      getNodeColor,
      disabled: computed(() => props.disabled)
    }
  }
})
</script>

<style scoped>
.hierarchical-select {
  position: relative;
  width: 100%;
}

.hierarchical-select-trigger {
  display: flex;
  justify-content: space-between;
  align-items: center;
  cursor: pointer;
  user-select: none;
  min-height: 38px;
  padding: 0.375rem 0.75rem;
}

.hierarchical-select-trigger:hover {
  border-color: #86b7fe;
}

.hierarchical-select-trigger.disabled {
  cursor: not-allowed;
  background-color: #e9ecef;
  opacity: 0.65;
}

.hierarchical-select-trigger.disabled:hover {
  border-color: #dee2e6;
}

.selected-value {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.hierarchical-dropdown {
  position: absolute;
  top: 100%;
  left: 0;
  right: 0;
  margin-top: 0.25rem;
  background: white;
  border: 1px solid #dee2e6;
  border-radius: 0.375rem;
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06);
  z-index: 1050;
  max-height: 400px;
  display: flex;
  flex-direction: column;
}

/* Hide default Quasar material arrow text */
:deep(.q-tree__arrow) {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  margin-right: 6px;
  font-size: 0; /* hide original text like 'play_arrow' */
  color: transparent;
  overflow: hidden;
}
:deep(.q-tree__arrow::before) {
  content: '▸';
  transition: transform 0.15s ease;
  font-size: 12px;
  color: #4b5563;
}
:deep(.q-tree__node--opened .q-tree__arrow::before) {
  transform: rotate(90deg);
}

.search-box {
  position: relative;
  padding: 0.75rem;
  border-bottom: 1px solid #e5e7eb;
}

.search-icon {
  position: absolute;
  right: 1.5rem;
  top: 50%;
  transform: translateY(-50%);
  color: #6b7280;
  pointer-events: none;
}

.tree-container {
  overflow-y: auto;
  padding: 0.5rem;
  flex: 1;
}

.tree-node-content {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.375rem 0.5rem;
  border-radius: 0.25rem;
  transition: background-color 0.15s ease;
}

.tree-node-content:hover {
  background-color: #f3f4f6;
}

.node-icon {
  font-size: 1rem;
  flex-shrink: 0;
}

.node-label {
  flex: 1;
  font-size: 0.875rem;
}

.clear-selection {
  padding: 0.5rem;
  border-top: 1px solid #e5e7eb;
  margin-top: 0.5rem;
}

/* Customize Quasar tree styles */
:deep(.q-tree) {
  font-size: 0.875rem;
}

:deep(.q-tree__node-header) {
  padding: 0.125rem;
}

:deep(.q-tree__node--selected .tree-node-content) {
  background-color: #dbeafe;
  color: #1e40af;
  font-weight: 500;
}

:deep(.q-tree__arrow) {
  color: #6b7280;
}

:deep(.q-tree__node-header:hover .tree-node-content) {
  background-color: #f3f4f6;
}

:deep(.q-tree__node--disabled) {
  opacity: 0.5;
  cursor: not-allowed;
}

:deep(.q-tree__node--disabled .tree-node-content:hover) {
  background-color: transparent;
}
</style>
